/**
 * Shared API types. Mirrors docs/api-phase0.md exactly. `camelCase` throughout
 * (CONVENTIONS.md §5). Money/quantity fields are always `string`.
 */

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  mustChangePassword: boolean;
}

export interface Tenant {
  id: string;
  name: string;
}

export interface Site {
  id: string;
  code: string;
}

export interface CompanyRef {
  id: string;
  name: string;
  isActive: boolean;
}

export interface Restrictions {
  maxBackdateDays: number | null;
  maxForwarddateDays: number | null;
  voucherAmountCeiling: string | null;
  maskPurchaseRate: boolean;
  maskCostAndMargin: boolean;
}

export interface SessionPayload {
  user: SessionUser;
  tenant: Tenant;
  site: Site;
  companies: CompanyRef[];
  activeCompanyId: string | null;
  permissions: string[];
  restrictions: Restrictions;
}

export type EntityType =
  | 'proprietorship'
  | 'partnership'
  | 'llp'
  | 'private_limited'
  | 'public_limited'
  | 'huf'
  | 'trust'
  | 'society'
  | 'aop_boi';

export type BusinessProfile =
  'trader' | 'distributor' | 'manufacturer' | 'job_worker' | 'services' | 'retailer' | 'custom';

export interface Company {
  id: string;
  name: string;
  legalName: string | null;
  pan: string | null;
  entityType: EntityType;
  booksBeginOn: string;
  baseCurrency: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateCode: string | null;
  pincode: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  isActive: boolean;
  revision: string;
  createdAt: string;
  updatedAt: string;
}

export const FEATURE_KEYS = [
  'maintain_inventory',
  'multi_godown',
  'batches_and_expiry',
  'serial_tracking',
  'bill_wise_details',
  'cost_centres',
  'budgets',
  'bom_manufacturing',
  'job_work',
  'order_processing',
  'actual_vs_billed_qty',
  'multiple_price_levels',
  'interest_calculation',
  'discount_columns',
  'additional_charges',
  'gst_enabled',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export interface CompanyFeatures {
  id: string;
  companyId: string;
  businessProfile: BusinessProfile;
  flags: Record<FeatureKey, boolean>;
  taxRoundingLevel: 'line' | 'invoice';
  allowNegativeStock: boolean;
  costingMethod: 'weighted_average' | 'fifo';
  revision: string;
}

export interface Branch {
  id: string;
  companyId: string;
  name: string;
  gstin: string | null;
  stateCode: string;
  registrationType: 'regular' | 'composition' | 'unregistered' | 'sez' | 'sez_developer';
  isPrimary: boolean;
  isActive: boolean;
  revision: string;
}

export interface FinancialYear {
  id: string;
  companyId: string;
  label: string;
  startsOn: string;
  endsOn: string;
  isClosed: boolean;
  closedAt: string | null;
  closedBy: string | null;
  revision: string;
}

export interface PeriodLock {
  id: string;
  companyId: string;
  lockedFrom: string;
  lockedTo: string;
  reason: string | null;
  lockedBy: string | null;
  lockedAt: string | null;
  revision: string;
}

export type LedgerType = 'party' | 'bank' | 'cash' | 'tax' | 'stock' | 'round_off' | 'general';

export type PartyRegistrationType =
  'regular' | 'composition' | 'unregistered' | 'sez' | 'sez_developer' | 'overseas' | 'deemed_export' | 'uin' | 'isd';

export interface AccountGroup {
  id: string;
  companyId: string;
  name: string;
  parentId: string | null;
  code: string | null;
  isPrimary: boolean;
  isSystem: boolean;
  affectsGrossProfit: boolean;
  nature: 'asset' | 'liability' | 'income' | 'expense';
  sortOrder: number | null;
  revision: string;
  children?: AccountGroup[];
}

export interface Ledger {
  id: string;
  companyId: string;
  name: string;
  alias: string | null;
  groupId: string;
  ledgerType: LedgerType;
  openingBalance: string;
  openingBalanceType: 'dr' | 'cr';
  isSystem: boolean;
  isActive: boolean;
  revision: string;
  // party fields
  gstin?: string | null;
  registrationType?: PartyRegistrationType | null;
  stateCode?: string | null;
  creditLimit?: string | null;
  creditDays?: number | null;
  // bank fields
  bankName?: string | null;
  accountNumber?: string | null;
  ifsc?: string | null;
  taxComponent?: string | null;
}

export interface User {
  id: string;
  email: string;
  fullName: string | null;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  revision: string;
}

export interface UserAssignment {
  roleId: string;
  companyId: string | null;
}

export interface UserOverride {
  permissionCode: string;
  effect: 'grant' | 'deny';
  companyId: string | null;
}

export interface UserDetail extends User {
  roles: UserAssignment[];
  companyIds: string[];
  overrides: UserOverride[];
  restrictions: Restrictions;
}

export interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  revision: string;
}

export interface PermissionCatalogueEntry {
  key: string;
  domain: string;
  object: string;
  action: string;
  label: string;
}

export interface AuditEntry {
  id: string;
  tableName: string;
  recordId: string;
  action: 'insert' | 'update' | 'delete';
  actorUserId: string | null;
  actorMissing: boolean;
  actorName?: string;
  occurredAt: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface SystemHealth {
  status: string;
  version: string;
  build: string;
  schemaVersion: string;
  databaseSizeBytes: number;
  rowCounts: Record<string, number>;
  uptimeSeconds: number;
  siteId: string;
  lastBackupAt: string | null;
  complianceDataDate: string | null;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// --------------------------------------------------------- vouchers ----

export type VoucherState = 'regular' | 'optional' | 'post_dated' | 'memorandum';
export type VoucherBaseBehaviour =
  | 'journal'
  | 'contra'
  | 'purchase'
  | 'sales'
  | 'receipt'
  | 'payment'
  | 'debit_note'
  | 'credit_note'
  | 'stock_journal'
  | 'material_out'
  | 'material_in'
  | 'delivery_note'
  | 'receipt_note'
  | 'physical_stock'
  | 'purchase_order'
  | 'sales_order'
  | 'quotation'
  | 'rejection_in'
  | 'rejection_out';

export interface VoucherType {
  id: string;
  companyId: string;
  code: string;
  name: string;
  baseBehaviourCode: VoucherBaseBehaviour;
  allowsFinancialEntries: boolean;
  allowsInventoryEntries: boolean;
  allowsBillReferences: boolean;
  isSystem: boolean;
  isActive: boolean;
  revision: string;
}

export interface VoucherLine {
  id: string;
  voucherId: string;
  ledgerId: string;
  lineNumber: number;
  debit: string;
  credit: string;
  narration: string;
  isActive: boolean;
  revision: string;
  createdAt: string;
  updatedAt: string;
  ledger?: Ledger;
}

export interface Voucher {
  id: string;
  companyId: string;
  branchId: string;
  financialYearId: string;
  voucherTypeId: string;
  voucherTypeCode: string;
  voucherTypeName: string;
  baseBehaviourCode: VoucherBaseBehaviour;
  financialYearLabel: string;
  voucherDate: string;
  voucherNumber: string;
  displayNumber: string;
  state: VoucherState;
  narration: string;
  reference: string | null;
  reversesVoucherId: string | null;
  autoReverseOn: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  revision: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  lines: VoucherLine[];
}

export interface VoucherLinePayload {
  ledgerId: string;
  debit: string;
  credit: string;
  narration?: string;
}

export interface CreateVoucherPayload {
  voucherTypeId: string;
  branchId: string;
  financialYearId: string;
  voucherDate: string;
  state?: VoucherState;
  narration?: string;
  reference?: string | null;
  lines: VoucherLinePayload[];
}

export interface UpdateVoucherPayload {
  revision: string;
  voucherDate?: string;
  state?: VoucherState;
  narration?: string;
  reference?: string | null;
  lines?: VoucherLinePayload[];
}

export interface CancelVoucherPayload {
  revision: string;
  reason: string;
}

export interface VoucherListParams {
  voucherTypeId?: string;
  baseBehaviour?: VoucherBaseBehaviour;
  from?: string;
  to?: string;
  ledgerId?: string;
  state?: VoucherState;
  cancelled?: boolean;
  limit?: number;
  offset?: number;
}

// ------------------------------------------------------------ reports ----

export const REPORT_CODES = ['day_book', 'trial_balance', 'ledger', 'group_summary', 'balance_sheet'] as const;
export type ReportCode = (typeof REPORT_CODES)[number];
export type ReportPreset =
  'today' | 'this_month' | 'this_quarter' | 'this_financial_year' | 'last_financial_year' | 'custom';
export type ReportColumnPeriod = 'single' | 'month' | 'quarter' | 'year';
export type ReportComparisonKind = 'none' | 'prior_period' | 'prior_year';

export interface ReportRange {
  from: string;
  to: string;
  preset: ReportPreset;
}

export interface ReportPeriod {
  key: string;
  label: string;
  from: string;
  to: string;
}

export interface AmountColumn extends ReportPeriod {
  debit: string;
  credit: string;
  closingDebit: string;
  closingCredit: string;
}

export interface TrialBalanceAmounts {
  openingDebit: string;
  openingCredit: string;
  debit: string;
  credit: string;
  closingDebit: string;
  closingCredit: string;
}

export interface TrialBalanceComparison extends TrialBalanceAmounts {
  range: ReportRange;
  absoluteVariance: string;
  percentageVariance: string | null;
}

export interface MovementComparison {
  range: ReportRange;
  debit: string;
  credit: string;
  debitAbsoluteVariance: string;
  debitPercentageVariance: string | null;
  creditAbsoluteVariance: string;
  creditPercentageVariance: string | null;
}

export interface DayBookTotals {
  enteredDebit: string;
  enteredCredit: string;
  effectiveDebit: string;
  effectiveCredit: string;
}

export interface DayBookPeriodColumn extends ReportPeriod, DayBookTotals {}

export interface DayBookComparison {
  range: ReportRange;
  entered: MovementComparison;
  effective: MovementComparison;
}

export interface BalanceSheetAmountColumn extends ReportPeriod {
  amount: string;
}

export interface BalanceSheetAmountComparison {
  range: ReportRange;
  amount: string;
  absoluteVariance: string;
  percentageVariance: string | null;
}

export interface BalanceSheetTotalsColumn extends ReportPeriod {
  totalAssets: string;
  totalLiabilities: string;
}

export interface BalanceSheetTotalsComparison {
  range: ReportRange;
  totalAssets: string;
  totalLiabilities: string;
  totalAssetsAbsoluteVariance: string;
  totalAssetsPercentageVariance: string | null;
  totalLiabilitiesAbsoluteVariance: string;
  totalLiabilitiesPercentageVariance: string | null;
}

export interface TrialBalanceRow extends TrialBalanceAmounts {
  ledgerId: string | null;
  ledgerName: string;
  groupId: string | null;
  sourceKind: 'ledger' | 'opening_balance_configuration';
  drillDown: 'ledger' | 'opening_balance_configuration';
  columns: AmountColumn[];
  comparison: TrialBalanceComparison | null;
}

export interface TrialBalanceReport {
  code: 'trial_balance';
  range: ReportRange;
  rows: TrialBalanceRow[];
  totals: TrialBalanceAmounts;
  comparison: (TrialBalanceAmounts & { range: ReportRange }) | null;
}

export interface DayBookRow {
  voucherId: string;
  voucherDate: string;
  voucherTypeId: string;
  voucherTypeName: string;
  displayNumber: string;
  state: string;
  cancelledAt: string | null;
  affectsBooks: boolean;
  narration: string;
  debit: string;
  credit: string;
}

export interface DayBookReport {
  code: 'day_book';
  range: ReportRange;
  rows: DayBookRow[];
  totals: DayBookTotals;
  columns: DayBookPeriodColumn[];
  comparison: DayBookComparison | null;
}

export interface LedgerReportRow {
  entryId: string;
  voucherId: string;
  voucherDate: string;
  displayNumber: string;
  narration: string;
  debit: string;
  credit: string;
  runningDebit: string;
  runningCredit: string;
}

export interface LedgerReport {
  code: 'ledger';
  range: ReportRange;
  ledgerId: string;
  ledgerName: string;
  openingDebit: string;
  openingCredit: string;
  rows: LedgerReportRow[];
  closingDebit: string;
  closingCredit: string;
  totals: TrialBalanceAmounts;
  columns: AmountColumn[];
  comparison: TrialBalanceComparison | null;
}

export interface GroupSummaryRow {
  groupId: string;
  parentId: string | null;
  name: string;
  nature: 'asset' | 'liability' | 'income' | 'expense';
  depth: number;
  debit: string;
  credit: string;
  closingDebit: string;
  closingCredit: string;
  ledgerIds: string[];
  columns: AmountColumn[];
  comparison: TrialBalanceComparison | null;
}

export interface GroupSummaryReport {
  code: 'group_summary';
  range: ReportRange;
  rows: GroupSummaryRow[];
  totals: TrialBalanceAmounts;
  comparison: (TrialBalanceAmounts & { range: ReportRange }) | null;
}

export interface BalanceSheetRow {
  groupId: string | null;
  parentId: string | null;
  name: string;
  nature: 'asset' | 'liability';
  depth: number;
  amount: string;
  columns: BalanceSheetAmountColumn[];
  comparison: BalanceSheetAmountComparison | null;
  ledgerIds: string[];
  sourceKind: 'account_group' | 'opening_balance_configuration' | 'current_profit_loss';
  drillDown: 'group' | 'opening_balance_configuration' | 'ledger_set';
}

export interface BalanceSheetReport {
  code: 'balance_sheet';
  range: ReportRange;
  assets: BalanceSheetRow[];
  liabilities: BalanceSheetRow[];
  totalAssets: string;
  totalLiabilities: string;
  columns: BalanceSheetTotalsColumn[];
  comparison: BalanceSheetTotalsComparison | null;
}

export interface ReportPreference {
  id: string | null;
  reportCode: ReportCode;
  preferences: Record<string, unknown>;
  revision: string | null;
}

export interface ReportQuery {
  preset?: ReportPreset;
  from?: string;
  to?: string;
  columnPeriod?: ReportColumnPeriod;
  comparison?: ReportComparisonKind;
  groupId?: string;
}

// ---------------------------------------------------- phase 2 masters ----
// Amounts, factors and GST rates remain decimal strings.  This keeps UI
// transport free of JavaScript floating-point arithmetic.
export interface ItemGroup {
  id: string;
  companyId: string;
  name: string;
  code: string | null;
  parentId: string | null;
  sortOrder: number | null;
  isActive: boolean;
  revision: string;
}
export interface UnitOfMeasure {
  id: string;
  companyId: string;
  code: string;
  name: string;
  symbol: string;
  dimension: 'count' | 'weight' | 'length' | 'volume' | 'area' | 'time' | 'compound' | 'other';
  numeratorUnitId: string | null;
  denominatorUnitId: string | null;
  allowsFraction: boolean;
  isActive: boolean;
  revision: string;
}
export type ItemKind = 'goods' | 'service';
export type GstTaxTreatment = 'taxable' | 'nil_rated' | 'exempt' | 'non_gst';
export interface ItemUnit {
  id?: string;
  revision?: string;
  unitOfMeasureId: string;
  role: 'base' | 'stock' | 'purchase' | 'sales';
  conversionFactor: string;
}
export interface ItemIdentifier {
  id?: string;
  revision?: string;
  kind: 'sku' | 'barcode' | 'ean' | 'part_number' | 'alias';
  value: string;
}
export interface ItemGstRate {
  id?: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  taxTreatment: GstTaxTreatment;
  ratePercentage: string;
  cessMode: 'percentage' | 'per_unit' | 'higher_of' | null;
  cessPercentage: string | null;
  cessPerUnit: string | null;
  revision?: string;
}
export interface Item {
  id: string;
  companyId: string;
  name: string;
  alias: string | null;
  itemGroupId: string | null;
  kind: ItemKind;
  hsnSacCode: string | null;
  baseUnitId: string | null;
  standardPurchaseRate: string | null;
  standardSalesRate: string | null;
  actualVsBilledQuantity: boolean;
  isActive: boolean;
  revision: string;
  units: ItemUnit[];
  identifiers: ItemIdentifier[];
  gstRates: ItemGstRate[];
}
export interface HsnSacRate {
  id: string;
  companyId: string;
  hsnSacCode: string;
  hsnSacKind: 'hsn' | 'sac';
  effectiveFrom: string;
  effectiveTo: string | null;
  taxTreatment: GstTaxTreatment;
  gstRate: string;
  cessType: 'ad_valorem' | 'specific' | 'higher_of';
  cessRate: string;
  cessAmount: string | null;
  isActive: boolean;
  revision: string;
}
export interface PartyAddress {
  id: string;
  companyId: string;
  partyLedgerId: string;
  label: string;
  addressKind: 'billing' | 'shipping' | 'other';
  recipientName: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  stateCode: string | null;
  pincode: string | null;
  country: string;
  gstin: string | null;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  isDefault: boolean;
  isActive: boolean;
  revision: string;
}
export interface VoucherClass {
  id: string;
  companyId: string;
  code: string;
  name: string;
  voucherTypeId: string;
  templateData: Record<string, unknown>;
  isActive: boolean;
  revision: string;
}
export interface InvoiceSeries {
  id: string;
  companyId: string;
  code: string;
  name: string;
  branchId: string;
  voucherTypeId: string;
  prefix: string;
  suffix: string;
  numberPadding: number;
  resetOnFinancialYear: boolean;
  isIrpCompatible: boolean;
  isActive: boolean;
  revision: string;
}
export interface InvoiceNumberingState {
  id: string;
  companyId: string;
  branchId: string;
  invoiceSeriesId: string;
  financialYearId: string;
  nextNumber: string;
  revision: string;
}
export interface GoodsPostingAccountMapping {
  id: string;
  companyId: string;
  purchaseLedgerId: string | null;
  salesLedgerId: string | null;
  inputCgstLedgerId: string | null;
  inputSgstLedgerId: string | null;
  inputUtgstLedgerId: string | null;
  inputIgstLedgerId: string | null;
  inputCessLedgerId: string | null;
  outputCgstLedgerId: string | null;
  outputSgstLedgerId: string | null;
  outputUtgstLedgerId: string | null;
  outputIgstLedgerId: string | null;
  outputCessLedgerId: string | null;
  roundOffLedgerId: string | null;
  revision: string;
}
export interface PrintTemplate {
  id: string;
  companyId: string;
  branchId: string | null;
  voucherTypeId: string | null;
  documentCode: string;
  name: string;
  paperSize: 'a4' | 'a5' | 'thermal_58' | 'thermal_80';
  templateDefinition: Record<string, unknown>;
  isDefault: boolean;
  isActive: boolean;
  revision: string;
}
export type ItemWritePayload = Omit<Item, 'id' | 'companyId' | 'revision' | 'isActive'> & { isActive?: boolean };
export type MasterWrite<T> = Omit<T, 'id' | 'companyId' | 'revision'>;

/** Exact Phase 2 HTTP contracts; UI setup helpers translate to/from these records. */
export interface ItemMasterWritePayload {
  itemGroupId?: string | null;
  code?: string | null;
  name: string;
  description?: string;
  itemKind?: ItemKind;
  hsnSacCode?: string | null;
  hsnSacKind?: 'hsn' | 'sac' | null;
  standardPurchaseRate?: string | null;
  standardSalesRate?: string | null;
  actualVsBilledQtyEnabled?: boolean;
  isActive?: boolean;
}
export interface ItemUnitWritePayload {
  unitId: string;
  conversionMode?: 'fixed' | 'variable';
  conversionToStock?: string | null;
  isStockUnit?: boolean;
  isPurchaseUnit?: boolean;
  isSalesUnit?: boolean;
  isActive?: boolean;
}
export interface ItemIdentifierWritePayload {
  partyLedgerId?: string | null;
  identifierType: ItemIdentifier['kind'];
  value: string;
  isActive?: boolean;
}
export interface ItemGstRateWritePayload {
  effectiveFrom: string;
  effectiveTo?: string | null;
  taxTreatment: GstTaxTreatment;
  gstRate: string;
  cessRate?: string;
  cessType?: 'ad_valorem' | 'specific' | 'higher_of';
  cessAmount?: string | null;
  isActive?: boolean;
}

// ------------------------------------------------ goods vouchers (Phase 2) ----
export type GoodsDirection = 'purchase' | 'sales';
export type GoodsVoucherState = 'regular' | 'optional' | 'post_dated' | 'memorandum';
export interface GoodsVoucherLinePayload {
  itemId: string;
  itemUnitId: string;
  /** Required by the physical Debit/Credit Note routes. */
  sourceInventoryEntryId?: string;
  actualQuantity: string;
  billedQuantity: string;
  freeQuantity: string;
  unitRate: string;
  discountPercent?: string;
  discountAmount?: string;
  discountTradeAmount?: string;
  itcEligibility?: 'eligible' | 'ineligible_section_17_5' | 'capital_goods' | 'input_service';
  narration?: string;
}
export interface GoodsAdditionalChargePayload {
  id?: string;
  kind: 'freight' | 'packing' | 'insurance' | 'loading' | 'handling' | 'other';
  amount: string;
  ledgerId: string;
  apportionmentBasis?: 'value' | 'quantity' | 'weight' | 'volume' | 'manual';
  capitaliseIntoStock?: boolean;
  narration?: string;
}
export interface GoodsBillReferencePayload {
  id?: string;
  referenceType: 'new_ref' | 'against_ref' | 'advance' | 'on_account';
  referenceNumber?: string;
  settlesBillReferenceId?: string;
  amount: string;
  dueDate?: string;
  narration?: string;
}
export interface GoodsVoucherPayload {
  voucherTypeId: string;
  voucherClassId?: string;
  branchId: string;
  financialYearId: string;
  voucherDate: string;
  state?: GoodsVoucherState;
  narration?: string;
  reference?: string | null;
  partyLedgerId: string;
  placeOfSupplyStateCode?: string;
  billingAddressId?: string;
  shippingAddressId?: string;
  /** Sales only. Omit when the server can resolve the sole active series. */
  invoiceSeriesId?: string;
  /** Physical Debit/Credit Note routes require the original goods voucher. */
  sourceVoucherId?: string;
  saleType?:
    | 'b2b'
    | 'b2c'
    | 'b2c_large'
    | 'export_with_lut'
    | 'export_with_payment'
    | 'sez_with_payment'
    | 'sez_without_payment'
    | 'deemed_export'
    | 'nil_rated'
    | 'exempt'
    | 'non_gst';
  purchaseType?: 'domestic' | 'import' | 'reverse_charge' | 'deemed_import';
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  selfInvoiceNumber?: string;
  reverseCharge?: boolean;
  invoiceDiscount?: string;
  additionalCharges?: GoodsAdditionalChargePayload[];
  lines: GoodsVoucherLinePayload[];
  billReferences?: GoodsBillReferencePayload[];
}
export interface GoodsVoucher {
  id: string;
  revision: string;
  companyId: string;
  branchId: string;
  financialYearId: string;
  voucherTypeId: string;
  voucherDate: string;
  voucherNumber: string;
  displayNumber: string;
  state: GoodsVoucherState;
  narration: string;
  reference: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  computed: {
    direction: GoodsDirection;
    computation: {
      totals: {
        counterpartyPayable: string;
        principalValueAfterDiscounts: string;
        additionalCharges?: string;
        assessableValue?: string;
        gstTaxableValue?: string;
        totalTax?: string;
        roundOff: string;
        taxes: Record<string, string>;
      };
      documentType: string;
      lines?: unknown[];
    };
    lineFacts?: unknown[];
    additionalCharges?: GoodsAdditionalChargePayload[];
    invoiceSeries?: { id?: string; code?: string; name?: string } | null;
    invoiceSnapshot?: { invoiceSeries?: { id?: string; code?: string; name?: string } | null };
  } | null;
  financialEntries: Array<{
    id: string;
    ledgerId: string;
    lineNumber: number;
    debit: string;
    credit: string;
    narration: string;
    isActive: boolean;
  }>;
  inventoryEntries: Array<Record<string, unknown>>;
  billReferences: GoodsBillReferencePayload[];
}
export interface GoodsVoucherTotals {
  counterpartyPayable: string;
  principalValueAfterDiscounts: string;
  additionalCharges?: string;
  assessableValue?: string;
  gstTaxableValue?: string;
  totalTax?: string;
  roundOff: string;
  taxes: Record<string, string>;
}
export interface GoodsVoucherPreview {
  preview: true;
  computed: NonNullable<GoodsVoucher['computed']>;
}
export interface GoodsVoucherRegisterItem {
  id: string;
  revision: string;
  voucherDate: string;
  displayNumber: string;
  cancelledAt: string | null;
  partyLedgerId: string;
  partyName: string;
  totals: GoodsVoucherTotals | null;
  drillTarget: string;
}
export interface GoodsVoucherRegister {
  direction: GoodsDirection;
  from: string;
  to: string;
  monthlySummary: Array<{
    month: string;
    count: number;
    cancelledCount: number;
    grossAssessableValue: string;
    netAssessableValue: string;
    grossTotal: string;
    netTotal: string;
  }>;
  totals: {
    grossAssessableValue: string;
    netAssessableValue: string;
    grossTotal: string;
    netTotal: string;
    cancelledCount: number;
  };
  items: GoodsVoucherRegisterItem[];
  count: number;
}

// -------------------------------------------------------- phase 3 money ----
// Money workflows deliberately transport amounts as decimal strings. These
// records mirror the server's money/reconciliation resources and keep the
// UI independent from floating point arithmetic.
export type MoneyVoucherKind = 'receipt' | 'payment' | 'debit_note' | 'credit_note';
export type InstrumentKind = 'cash' | 'cheque' | 'neft' | 'rtgs' | 'imps' | 'upi' | 'card' | 'other';

export interface MoneyVoucherInstrument {
  kind: InstrumentKind;
  number?: string | null;
  instrumentDate?: string | null;
  bankName?: string | null;
  utr?: string | null;
  reference?: string | null;
  clearingDate?: string | null;
  isPostDated?: boolean;
}

export type BillAllocationKind = 'against_ref' | 'advance' | 'on_account' | 'new_ref';
export type MoneyAdjustmentKind = 'settlement_discount' | 'tds' | 'bank_charges' | 'round_off';

export interface MoneyAdjustment {
  kind: MoneyAdjustmentKind;
  ledgerId: string;
  amount: string;
  side: 'debit' | 'credit';
}

export interface BillAllocation {
  id?: string;
  billId?: string | null;
  billNumber?: string | null;
  billDate?: string | null;
  dueDate?: string | null;
  amount: string;
  discount?: string;
  tds?: string;
  kind: BillAllocationKind;
  narration?: string;
}

export interface MoneyVoucherPayload {
  /** Resolved server voucher type; the desk may derive this from the kind. */
  voucherTypeId?: string;
  kind: MoneyVoucherKind;
  branchId: string;
  financialYearId: string;
  voucherDate: string;
  partyLedgerId: string;
  bankOrCashLedgerId: string;
  amount: string;
  instrument?: MoneyVoucherInstrument;
  allocations?: BillAllocation[];
  settlementDiscount?: string;
  tds?: string;
  bankCharges?: string;
  roundOff?: string;
  reference?: string | null;
  narration?: string;
  state?: VoucherState;
  againstVoucherId?: string | null;
  reason?: string | null;
  lines?: VoucherLinePayload[];
  billReferences?: Array<BillAllocation & {
    partyLedgerId: string;
    direction: 'receivable' | 'payable';
    referenceType: BillAllocationKind;
    referenceNumber?: string;
    settlesBillReferenceId?: string;
  }>;
  adjustments?: MoneyAdjustment[];
}

/** Exact POST /money-vouchers contract. UI-only fields stay out of transport. */
export interface MoneyVoucherCreatePayload {
  voucherTypeId: string;
  branchId: string;
  financialYearId: string;
  voucherDate: string;
  state?: VoucherState;
  narration?: string;
  reference?: string | null;
  lines: VoucherLinePayload[];
  billReferences?: Array<{
    partyLedgerId: string;
    referenceType: BillAllocationKind;
    direction: 'receivable' | 'payable';
    amount: string;
    referenceNumber?: string | null;
    settlesBillReferenceId?: string | null;
    dueDate?: string | null;
    narration?: string;
  }>;
  instrument?: {
    accountLedgerId: string;
    instrumentType: 'cash' | 'cheque' | 'neft' | 'rtgs' | 'imps' | 'upi' | 'card' | 'other';
    instrumentNumber?: string | null;
    instrumentDate?: string | null;
    clearingDate?: string | null;
    bankName?: string | null;
    referenceNumber?: string | null;
    notes?: string;
  } | null;
  settlementNote?: string | null;
  adjustments?: MoneyAdjustment[];
}

export interface MoneyVoucher extends MoneyVoucherPayload {
  id: string;
  revision: string;
  voucherNumber: string;
  displayNumber: string;
  voucherTypeName: string;
  createdAt: string;
  updatedAt: string;
  cancelledAt: string | null;
  affectsBooks: boolean;
}

export interface OutstandingBill {
  id: string;
  partyLedgerId: string;
  partyName: string;
  billNumber: string;
  billDate: string;
  dueDate: string | null;
  originalAmount: string;
  settledAmount: string;
  outstandingAmount: string;
  overdueDays: number;
  creditDays: number | null;
  lastContactedOn: string | null;
  followUpNote: string | null;
}

export interface OutstandingParty {
  partyLedgerId: string;
  partyName: string;
  billCount: number;
  outstandingAmount: string;
  overdueAmount: string;
  oldestDueOn: string | null;
  lastContactedOn: string | null;
  followUpNote: string | null;
  bills: OutstandingBill[];
}

export interface OutstandingReport {
  asOf: string;
  side: 'receivable' | 'payable';
  basis: 'bill_date' | 'due_date';
  buckets: Array<{ key: string; label: string; amount: string; count: number }>;
  parties: OutstandingParty[];
  totals: { outstanding: string; overdue: string; current: string };
}

export interface InterestPreview {
  partyLedgerId: string;
  ratePercentage: string;
  mode: 'simple' | 'compound';
  from: string;
  to: string;
  baseAmount: string;
  interestAmount: string;
  totalAmount: string;
}

export interface BookEntry {
  id: string;
  voucherId: string;
  voucherDate: string;
  displayNumber: string;
  ledgerName: string;
  partyName: string | null;
  narration: string;
  debit: string;
  credit: string;
  runningBalance: string;
  reconciled: boolean;
  reconciliationDate: string | null;
  instrumentReference: string | null;
}

export interface BookReport {
  book: 'cash' | 'bank';
  ledgerId: string;
  ledgerName: string;
  from: string;
  to: string;
  openingBalance: string;
  closingBalance: string;
  reconciledBalance: string;
  unreconciledBalance: string;
  negativeBalance: boolean;
  entries: BookEntry[];
}

export interface PdcEntry {
  id: string;
  direction: 'receivable' | 'payable';
  partyLedgerId: string;
  partyName: string;
  chequeNumber: string;
  chequeDate: string;
  clearingDate: string;
  bankName: string | null;
  amount: string | null;
  status: 'pending' | 'cleared' | 'bounced' | 'cancelled';
  voucherId: string;
  revision?: string | null;
}

export interface BankStatementLine {
  id: string;
  matchId?: string | null;
  revision?: string | null;
  fingerprint: string;
  transactionDate: string;
  valueDate: string | null;
  reference: string;
  description: string;
  debit: string;
  credit: string;
  balance: string | null;
  status: 'unmatched' | 'probable' | 'matched' | 'ignored';
  matchConfidence: 'exact' | 'probable' | 'manual' | null;
  matchedVoucherIds: string[];
}

export interface BankMappingProfile {
  id: string;
  name: string;
  bankLedgerId: string;
  fileFormat?: 'csv' | 'xlsx' | 'manual';
  columnMapping?: Record<string, unknown>;
  skipRows?: number;
  revision?: string;
  dateColumn: string;
  referenceColumn: string;
  descriptionColumn: string;
  debitColumn: string | null;
  creditColumn: string | null;
  amountColumn: string | null;
  dateFormat: string;
}

export interface BankMappingProfileWrite {
  id?: string;
  revision?: string;
  bankLedgerId: string;
  name: string;
  fileFormat: 'csv' | 'xlsx' | 'manual';
  columnMapping: Record<string, unknown>;
  dateFormat?: string | null;
  skipRows?: number;
}

export interface ReconciliationMatchCandidate {
  voucherId: string;
  displayNumber: string;
  voucherDate: string;
  narration: string;
  amount: string;
  reference: string | null;
  confidence: 'exact' | 'probable' | 'manual';
  differenceAmount?: string;
  withinTolerance?: boolean;
  toleranceAmount?: string;
}

export interface BankReconciliationSettings {
  id: string | null;
  bankLedgerId: string;
  toleranceAmount: string;
  bankChargesLedgerId: string | null;
  roundOffLedgerId: string | null;
  revision: string | null;
}

export interface ReconciliationReport {
  bankLedgerId: string;
  statementId?: string | null;
  bankLedgerName: string;
  from: string;
  to: string;
  statementClosingBalance: string;
  bookBalance: string;
  reconciledBookBalance: string;
  unclearedIssued: string;
  unclearedDeposits: string;
  directCredits: string;
  bankCharges: string;
  lines: BankStatementLine[];
  candidates: Record<string, ReconciliationMatchCandidate[]>;
  mappingProfiles: BankMappingProfile[];
  settings?: BankReconciliationSettings | null;
}

export interface ReconciliationMutationPayload {
  statementLineIds: string[];
  voucherIds: string[];
  reconciliationDate: string;
  tolerance?: string;
}
